import React, { useContext } from 'react';

import {
    IVendorBillItemsSidebarFilters,
    IVendorBillItemsSidebarFiltersContext,
    VendorBillItemSidebarFiltersContext,
} from './VendorBillItemSidebarFiltersContext';
import { useSidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';
import { CollapseFilterWrapper } from '../../../../../components/sidebar-filters/CollapseFilterWrapper';
import { MarketFilterSelect } from '../../../../../components/sidebar-filters/filter-select/MarketFilterSelect';
import { ProgramFilterSelect } from '../../../../../components/sidebar-filters/filter-select/ProgramFilterSelect';
import { UtilityFilterSelect } from '../../../../../components/sidebar-filters/filter-select/UtilityFilterSelect';
import { HasEventFilterSelect } from '../../../../../components/sidebar-filters/filter-select/HasEventFilterSelect';
import { DateRangeWithCustomOptionFilterSelect } from '../../../../../components/sidebar-filters/filter-select/DateRangeWithCustomOptionFilterSelect';
import { TransactionIdFilterSelect } from '../../../../../components/sidebar-filters/filter-select/TransactionIdFilterSelect';

export const VendorBillItemFilterKeys = {
    // filters
    period: 'period',
    markets: 'markets',
    utilities: 'utilities',
    programs: 'programs',
    transactionIds: 'transactionIds',

    // options
    event: 'event',
} as const;

export type TVendorBillItemFilterKey = (typeof VendorBillItemFilterKeys)[keyof typeof VendorBillItemFilterKeys];

export const VendorBillItemFilterLabels = {
    [VendorBillItemFilterKeys.period]: 'Period Range',
    [VendorBillItemFilterKeys.markets]: 'Market',
    [VendorBillItemFilterKeys.utilities]: 'Utility',
    [VendorBillItemFilterKeys.programs]: 'Program',
    [VendorBillItemFilterKeys.transactionIds]: 'Transaction Id',
    [VendorBillItemFilterKeys.event]: 'Event',
} as const;

interface IVendorBillItemsSidebarFiltersProps {
    onFilterChange: (filters: IVendorBillItemsSidebarFilters) => void;
}

export const VendorBillItemsSidebarFilters: React.FC<IVendorBillItemsSidebarFiltersProps> = ({ onFilterChange }) => {
    const { filters, handleFilterChange } = useContext<IVendorBillItemsSidebarFiltersContext>(VendorBillItemSidebarFiltersContext);
    const {
        isLoading: isFiltersLoading,
        marketOptions,
        vendorBillItemPeriodOptions,
        vendorBillItemProgramOptions,
        vendorBillItemUtilityOptions,
        vendorBillTransactionIdOptions,
        hasEventOptions,
    } = useSidebarFilterOptionsContext();

    const handleVendorBillItemsFilterChange = (key: string, value: unknown) => {
        handleFilterChange(key, value);
        onFilterChange({ ...filters, [key]: value });
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', overflowY: 'auto' }}>
            {/* Filters */}
            <CollapseFilterWrapper label="Filters">
                <DateRangeWithCustomOptionFilterSelect
                    height={220}
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.period]}
                    value={filters[VendorBillItemFilterKeys.period] || null}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.period, value)}
                    options={vendorBillItemPeriodOptions}
                    dateFormat={'MMM YYYY'}
                    pickerMode={'month'}
                />
                <MarketFilterSelect
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.markets]}
                    value={filters[VendorBillItemFilterKeys.markets] || []}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.markets, value)}
                    options={marketOptions}
                    isLoading={isFiltersLoading}
                />
                <ProgramFilterSelect
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.programs]}
                    value={filters[VendorBillItemFilterKeys.programs] || []}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.programs, value)}
                    options={vendorBillItemProgramOptions}
                    isLoading={isFiltersLoading}
                />
                <UtilityFilterSelect
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.utilities]}
                    value={filters[VendorBillItemFilterKeys.utilities] || []}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.utilities, value)}
                    options={vendorBillItemUtilityOptions}
                    isLoading={isFiltersLoading}
                />
                <TransactionIdFilterSelect
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.transactionIds]}
                    value={filters[VendorBillItemFilterKeys.transactionIds] || []}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.transactionIds, value)}
                    options={vendorBillTransactionIdOptions}
                    isLoading={isFiltersLoading}
                />
            </CollapseFilterWrapper>
            <CollapseFilterWrapper label="Options">
                <HasEventFilterSelect
                    label={VendorBillItemFilterLabels[VendorBillItemFilterKeys.event]}
                    value={filters[VendorBillItemFilterKeys.event] || null}
                    onChange={value => handleVendorBillItemsFilterChange(VendorBillItemFilterKeys.event, value)}
                    options={hasEventOptions}
                    isLoading={isFiltersLoading}
                />
            </CollapseFilterWrapper>
        </div>
    );
};
